// Create / edit one authorization.
//
// The form mirrors authorization_service._clean rather than guessing at its own
// rules: same four-word status vocabulary, same two auth types, same three
// utilization bases. Client-side validation exists to save a round trip, never as
// the boundary — the server validates everything again and its message is what
// gets shown when they disagree.
//
// `utilization_basis` is on the form deliberately. It is what decides whether the
// counter counts visits, units or procedures, and a biller who mis-sets it gets a
// countdown that is wrong by a factor of five. It is not a field to hide.

import React, { useRef, useState } from "react";

import { linesBody, linesFrom } from "./api";
import { API_URL, errorFrom } from "./AuthBits";
import {
  LinesEditor,
  formLinesFrom,
  parsedLines,
  validateLines,
} from "./DisciplineLines";
import PatientPicker from "./PatientPicker";

const AUTH_TYPES = [
  { value: "prior_auth", label: "Prior authorization" },
  { value: "referral", label: "Referral" },
];

const BASES = [
  { value: "per_encounter", label: "Visits — one per date of service" },
  { value: "per_unit", label: "Units — the units billed on each line" },
  { value: "per_cpt", label: "Procedures — one per matching line" },
];

const STATUSES = ["active", "expired", "exhausted", "revoked"];

function initialForm(authorization) {
  return {
    patient_id: authorization?.patient_id ?? "",
    patient_name: authorization?.patient_name ?? "",
    practice_payer_id: authorization?.practice_payer_id ?? "",
    payer_name: authorization?.payer_name ?? "",
    auth_type: authorization?.auth_type ?? "prior_auth",
    auth_number: authorization?.auth_number ?? "",
    authorized_units: authorization?.authorized_units ?? "",
    utilization_basis: authorization?.utilization_basis ?? "per_encounter",
    authorized_services: (authorization?.authorized_services || []).join(", "),
    effective_from: authorization?.effective_from ?? "",
    effective_to: authorization?.effective_to ?? "",
    status: authorization?.status ?? "active",
    referring_provider_name: authorization?.referring_provider_name ?? "",
    referring_provider_npi: authorization?.referring_provider_npi ?? "",
    notes: authorization?.notes ?? "",
    lines: formLinesFrom(linesFrom(authorization)),
  };
}

export default function AuthorizationModal({
  practiceId,
  authorization,
  payers,
  payersFailed,
  onClose,
  onSaved,
  // Per-discipline lines are the auth-advisor sprint's; with the feature off the
  // editor is not rendered and `lines` never goes on the wire.
  showLines = false,
}) {
  // `.id`, not merely truthiness: the page also hands this a PREFILL object —
  // an unsaved draft built from a missing-auth alert — which has patient, payer
  // and a date but no id. Treating that as an edit would PUT to
  // /authorizations/undefined.
  const editing = Boolean(authorization && authorization.id);
  const [form, setForm] = useState(() => initialForm(authorization));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // A REF, not the state. `if (saving) return` reads a render-time closure, so
  // three synchronous submits all see `saving === false` and all three POST —
  // three rows for one authorization, which triples the capacity the counter
  // believes in. A real triple-click is stopped by `disabled`, but
  // requestSubmit() and dispatchEvent() are not, and password managers,
  // extensions and accessibility tooling do call them.
  const savingRef = useRef(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // A whole number, or nothing. This is not politeness about input format: the
  // body is built with Number() and serialised with JSON.stringify, and
  // JSON.stringify(NaN) is `null`. So "10 visits" typed into Approved amount
  // would reach the server as authorized_units: null, the server would accept
  // it as an explicit clear, and the biller would get a green "added" on an
  // authorization that now has NO CAP and can therefore never be reported as
  // exhausted. The same typo in Patient id detaches the authorization from its
  // patient. Both fail silently and in the direction that costs money, so they
  // are refused here rather than coerced.
  const wholeNumber = (value) => {
    const text = String(value).trim();
    if (text === "") return null;
    return /^\d+$/.test(text) ? Number(text) : NaN;
  };

  const validate = () => {
    if (!form.auth_number.trim()) return "An authorization number is required.";
    if (!editing && !form.patient_id && !form.practice_payer_id) {
      return "An authorization needs a patient or a payer.";
    }
    if (
      form.effective_from &&
      form.effective_to &&
      form.effective_to < form.effective_from
    ) {
      return "The end date cannot be before the start date.";
    }
    if (Number.isNaN(wholeNumber(form.authorized_units))) {
      return "Approved amount must be a whole number of visits, units or procedures — or blank for no cap.";
    }
    if (Number.isNaN(wholeNumber(form.patient_id))) {
      return "Patient id must be a whole number.";
    }
    if (
      form.referring_provider_npi &&
      !/^\d{10}$/.test(form.referring_provider_npi)
    ) {
      return "A referring provider NPI is exactly 10 digits.";
    }
    if (showLines) {
      const lineProblem = validateLines(form.lines);
      if (lineProblem) return lineProblem;
    }
    return null;
  };

  const body = () => {
    const out = {
      auth_type: form.auth_type,
      auth_number: form.auth_number.trim(),
      utilization_basis: form.utilization_basis,
      status: form.status,
      // "" means "clear it", which the server reads as None. Sending the empty
      // string rather than omitting the key is what makes clearing a date work.
      effective_from: form.effective_from || null,
      effective_to: form.effective_to || null,
      authorized_units: wholeNumber(form.authorized_units),
      authorized_services: form.authorized_services
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      referring_provider_name: form.referring_provider_name.trim() || null,
      referring_provider_npi: form.referring_provider_npi.trim() || null,
      notes: form.notes.trim() || null,
    };

    // The two LINK fields need different rules for create and edit, and getting
    // that wrong fails in both directions.
    //
    // CREATE sends whatever is set. It cannot use the changed-only rule below:
    // a prefilled create (from a missing-auth alert) starts with the form EQUAL
    // to its seed, so "unchanged" would drop both links and the server would
    // refuse the row for having neither a patient nor a payer.
    //
    // EDIT sends only what the form actually changed, including an explicit
    // null to clear — so "Not linked to a payer" really unlinks instead of
    // returning a cheerful 200 that did nothing, and an unrelated edit stops
    // resending an unchanged payer, which would make the server overwrite a
    // hand-typed payer_name ("Aetna Better Health of MI") with the payer row's
    // short name.
    const asText = (v) => (v === null || v === undefined ? "" : String(v));
    const patientChanged =
      !editing || asText(authorization?.patient_id) !== asText(form.patient_id);
    const payerChanged =
      !editing ||
      asText(authorization?.practice_payer_id) !== asText(form.practice_payer_id);

    if (patientChanged && (!editing ? form.patient_id !== "" : true)) {
      out.patient_id = wholeNumber(form.patient_id);
    }
    if (payerChanged && (!editing ? form.practice_payer_id !== "" : true)) {
      out.practice_payer_id =
        form.practice_payer_id === "" ? null : Number(form.practice_payer_id);
      // Clearing the link must clear the denormalised name with it. The server
      // fills payer_name only when a payer is being SET, so without this the
      // row keeps naming a payer it is no longer linked to — while an unlinked
      // auth silently covers EVERY payer for that patient.
      if (out.practice_payer_id === null) out.payer_name = null;
    }

    // Lines follow the same key-presence rule. CREATE sends them when there are
    // any. EDIT sends the whole set only when it changed — including an empty
    // list, which removes every line — so an unrelated edit never rewrites lines
    // a feed or another biller set.
    if (showLines) {
      const next = linesBody(parsedLines(form.lines));
      if (!editing) {
        if (next.length > 0) out.lines = next;
      } else {
        const before = linesBody(linesFrom(authorization));
        if (JSON.stringify(before) !== JSON.stringify(next)) out.lines = next;
      }
    }
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const url = editing
        ? `${API_URL}/api/practices/${practiceId}/authorizations/${authorization.id}`
        : `${API_URL}/api/practices/${practiceId}/authorizations`;
      const response = await fetch(url, {
        method: editing ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body()),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        setError(
          errorFrom(response, payload, "Could not save the authorization"),
        );
        return;
      }
      const saved = (payload && payload.authorization) || {};
      onSaved(
        editing
          ? `Authorization ${saved.auth_number || ""} updated.`
          : `Authorization ${saved.auth_number || ""} added.`,
      );
    } catch (err) {
      setError("Could not reach the server to save it.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    // Closing while a save is in flight threw the result away: the error landed
    // on an unmounted component and the biller was told nothing about a write
    // that did not happen. Cancel and Save were already disabled; the × and the
    // overlay were not.
    <div
      className="auth-modal-overlay"
      onClick={saving ? undefined : onClose}
    >
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h3>{editing ? "Edit authorization" : "Add authorization"}</h3>
          <button
            type="button"
            className="auth-modal-close"
            onClick={onClose}
            // Closing mid-save threw the result away: the error landed on an
            // unmounted component and the biller was told nothing about a write
            // that did not happen. Cancel and Save were already disabled; this
            // and the overlay were the two ways out that were not.
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="auth-modal-body">
            {error && (
              <div className="auth-message auth-message-error auth-message-inline">
                {error}
              </div>
            )}

            <div className="auth-field-row">
              <label className="auth-field">
                <span>Type</span>
                <select
                  className="auth-input"
                  value={form.auth_type}
                  onChange={set("auth_type")}
                >
                  {AUTH_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="auth-field">
                <span>
                  Number <em>required</em>
                </span>
                <input
                  className="auth-input"
                  value={form.auth_number}
                  onChange={set("auth_number")}
                  maxLength={50}
                  placeholder="The number the payer gave you"
                />
              </label>
            </div>

            <div className="auth-field-row">
              <PatientPicker
                practiceId={practiceId}
                value={form.patient_id}
                // `form.patient_name`, NOT the row's. The row's is frozen at open, so
                // picking a different patient re-rendered the picker with the
                // ORIGINAL name beside the NEW id — the form naming one patient
                // while filing the authorization against another, on the field
                // that decides whose counter this feeds.
                displayName={form.patient_name}
                onChange={(id, name) =>
                  setForm((f) => ({ ...f, patient_id: id, patient_name: name }))
                }
              />
              <label className="auth-field auth-field-wide">
                <span>Payer</span>
                <select
                  className="auth-input"
                  value={form.practice_payer_id}
                  onChange={set("practice_payer_id")}
                >
                  <option value="">Not linked to a payer</option>
                  {(payers || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.payer_name}
                      {p.requires_auth ? " — requires auth" : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="auth-field-hint">
              One of patient or payer is required. The payer's display name is
              filled in from the payer you pick.
            </p>
            {payersFailed && (
              <p className="auth-field-hint auth-field-hint-warn">
                The payer list could not be loaded, so the dropdown is empty. An
                authorization saved without a payer link will not clear that payer's
                missing-authorization alert — reload before saving if you need the link.
              </p>
            )}

            <div className="auth-field-row">
              <label className="auth-field">
                <span>Valid from</span>
                <input
                  type="date"
                  className="auth-input"
                  value={form.effective_from || ""}
                  onChange={set("effective_from")}
                />
              </label>
              <label className="auth-field">
                <span>Valid to</span>
                <input
                  type="date"
                  className="auth-input"
                  value={form.effective_to || ""}
                  onChange={set("effective_to")}
                />
              </label>
              <label className="auth-field">
                <span>Status</span>
                <select
                  className="auth-input"
                  value={form.status}
                  onChange={set("status")}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="auth-field-row">
              <label className="auth-field">
                <span>Approved amount</span>
                <input
                  className="auth-input"
                  value={form.authorized_units}
                  onChange={set("authorized_units")}
                  inputMode="numeric"
                  placeholder="Leave blank for no cap"
                />
              </label>
              <label className="auth-field auth-field-wide">
                <span>Counted as</span>
                <select
                  className="auth-input"
                  value={form.utilization_basis}
                  onChange={set("utilization_basis")}
                >
                  {BASES.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {!form.effective_from && (
              <p className="auth-field-hint auth-field-hint-warn">
                With no start date, every visit this patient has ever had counts against
                this authorization — including ones from before the payer approved it.
                Set one unless the payer really did back-date it indefinitely.
              </p>

            )}

            <p className="auth-field-hint">
              This is what the countdown counts. Payers usually approve{" "}
              <strong>visits</strong>, and physical therapy bills four or five
              units on one visit — so counting units where the payer meant
              visits reads the authorization as spent five times too fast. Leave
              the amount blank when the payer set no limit; that shows as “no
              cap” rather than as zero left.
            </p>

            {showLines && (
              <LinesEditor
                lines={form.lines}
                onChange={(lines) => setForm((f) => ({ ...f, lines }))}
              />
            )}

            <label className="auth-field auth-field-full">
              <span>Covered CPT codes</span>
              <input
                className="auth-input"
                value={form.authorized_services}
                onChange={set("authorized_services")}
                placeholder="97110, 97140 — leave blank for every code"
              />
            </label>
            <p className="auth-field-hint">
              Only visits with one of these codes count against the
              authorization. Blank means every code counts.
            </p>

            <div className="auth-field-row">
              <label className="auth-field">
                <span>Referring provider</span>
                <input
                  className="auth-input"
                  value={form.referring_provider_name}
                  onChange={set("referring_provider_name")}
                  maxLength={200}
                />
              </label>
              <label className="auth-field">
                <span>Referring NPI</span>
                <input
                  className="auth-input"
                  value={form.referring_provider_npi}
                  onChange={set("referring_provider_npi")}
                  maxLength={10}
                  inputMode="numeric"
                />
              </label>
            </div>

            <label className="auth-field auth-field-full">
              <span>Notes</span>
              <textarea
                className="auth-input auth-textarea"
                value={form.notes}
                onChange={set("notes")}
                rows={3}
                placeholder="Who you spoke to, what the payer said"
              />
            </label>
          </div>
          <div className="auth-modal-footer">
            <button
              type="button"
              className="auth-btn"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={saving}
            >
              {saving
                ? "Saving…"
                : editing
                  ? "Save changes"
                  : "Add authorization"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
